import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";
import { Card, Skeleton, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { formatDate } from "../../services/util";
import { FaRegSmile } from "react-icons/fa";
import { FaRegFaceMeh } from "react-icons/fa6";
import { FaRegFaceFrown } from "react-icons/fa6";

const WidgetSkeletons = () => {
    return Array.from({ length: 5 }).map((_, index) => (
        <Card key={index} className="dashboard-counts">
            <div className="dashboard-counts-card-body">
                <Skeleton.Avatar active shape="square" size={48} />
                <Skeleton
                    active
                    title={{ width: 48 }}
                    paragraph={{ rows: 1, width: "70%" }}
                />
            </div>
        </Card>
    ));
};

const getDateRangePayload = ([startDate, endDate]) => {
    const start = dayjs(startDate).startOf("day");
    const end = dayjs(endDate).startOf("day");
    const dayDiff = end.diff(start, "day");
    const previousStart = start.subtract(dayDiff === 0 ? 1 : dayDiff, "day");
    const previousEnd = start.subtract(1, "day");

    return {
        start_date: formatDate(start),
        end_date: formatDate(end),
        previous_start_date: formatDate(previousStart),
        previous_end_date: formatDate(previousEnd),
    };
};

function CustomWidgets({ grids, configs, onData, transactionChartFilter }) {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const navigate = useNavigate();
    const { queryKey, url } = configs;
    const {
        selectedDates,
        selectedHotels,
        selectedHotelType,
        selectedHotelGroup,
        selectedServiceModel,
        selectedHotelActiveType,
        selectedCallCenters,
        selectedShifts,
        kioskSelection,
        setSelectedCheckinType,
    } = useContext(SettingContext);
    const hotel_ids = selectedHotels;

    const [widgetData, setWidgetData] = useState([]);
    const [defaultFormData, setDefaultFormData] = useState({
        staff_id,
        hotel_ids: hotel_ids,
        all_selected: hotel_ids.length > 0 ? false : true,
        ...getDateRangePayload(selectedDates),
        hotel_type: selectedHotelType,
        hotel_active_type: selectedHotelActiveType,
        call_centers: selectedCallCenters,
        shift_types: selectedShifts,
        ...(selectedHotelType == "kiosk"
            ? { calling_hotel_type: kioskSelection }
            : {}),
        ...(url == "getDashboardReviewsCounts"
            ? { curveDataFor: transactionChartFilter || "all" }
            : {}),
        group_ids: selectedHotelGroup.includes("all") ? [] : selectedHotelGroup,
        service_model: selectedServiceModel.includes("all")
            ? []
            : selectedServiceModel,
    });

    const {
        status,
        data: widgetDataRes,
        refetch,
        isFetching,
    } = useQuery(
        queryKey,
        async () => {
            return await HTTP(`/dashboard/${url}`, { ...defaultFormData });
        },
        {
            enabled: false,
        },
    );

    useEffect(() => {
        refetch();
    }, [defaultFormData]);

    useEffect(() => {
        const hotel_ids = selectedHotels;
        setDefaultFormData((previous) => ({
            ...previous,
            page: 0,
            hotel_ids,
            all_selected: hotel_ids.length === 0 ? true : false,
        }));
    }, [selectedHotels]);

    useEffect(() => {
        if (url === "getDashboardReviewsCounts")
            setDefaultFormData((previous) => ({
                ...previous,
                curveDataFor: transactionChartFilter || "all",
            }));
    }, [transactionChartFilter]);

    useEffect(() => {
        setDefaultFormData((previous) => ({
            ...previous,
            calling_hotel_type: kioskSelection,
        }));
    }, [kioskSelection]);

    useEffect(() => {
        setDefaultFormData((previous) => ({
            ...previous,
            hotel_type: selectedHotelType,
            ...(selectedHotelType == "kiosk"
                ? { calling_hotel_type: kioskSelection }
                : {}),
        }));
    }, [selectedHotelType]);

    useEffect(() => {
        setDefaultFormData((previous) => ({
            ...previous,
            hotel_active_type: selectedHotelActiveType,
        }));
    }, [selectedHotelActiveType]);

    useEffect(() => {
        setDefaultFormData((previous) => ({
            ...previous,
            call_centers: selectedCallCenters,
        }));
    }, [selectedCallCenters]);

    useEffect(() => {
        setDefaultFormData((previous) => ({
            ...previous,
            group_ids: selectedHotelGroup.includes("all")
                ? []
                : selectedHotelGroup,
        }));
    }, [selectedHotelGroup]);

    useEffect(() => {
        setDefaultFormData((previous) => ({
            ...previous,
            service_model: selectedServiceModel.includes("all")
                ? []
                : selectedServiceModel,
        }));
    }, [selectedServiceModel]);

    useEffect(() => {
        setDefaultFormData((previous) => ({
            ...previous,
            ...getDateRangePayload(selectedDates),
        }));
    }, [selectedDates]);

    useEffect(() => {
        if (status === "success") {
            let { data: widgetData } = widgetDataRes;
            onData?.(widgetData, configs);
            const formatedWidgetData = grids.reduce((previous, current) => {
                if (widgetData?.[current.key] !== undefined) {
                    current.value = widgetData[current.key] || 0;
                    current.diff = widgetData[`${current.key}_diff`] || 0;
                    if (current.key == "spticket_count") {
                        current.spticket_total_count =
                            widgetData[`spticket_total_count`] || 0;
                    }
                    if (current.key == "reviews") {
                        current.excellent_count =
                            widgetData[`excel_count`] || 0;
                        current.good_count = widgetData[`good_count`] || 0;
                        current.poor_count = widgetData[`poor_count`] || 0;
                        current.excellent_count_percentage =
                            widgetData[`excel_count_percentage`] || 0;
                        current.good_count_percentage =
                            widgetData[`good_count_percentage`] || 0;
                        current.poor_count_percentage =
                            widgetData[`poor_count_percentage`] || 0;
                    }
                    previous.push(current);
                }
                return previous;
            }, []);
            setWidgetData(formatedWidgetData);
        }
    }, [status, widgetDataRes]);

    function getWidgetDetails(e) {
        if (
            e.currentTarget.getAttribute("id") === "staff_user_count" ||
            e.currentTarget.getAttribute("id") === "guest_user_count" ||
            e.currentTarget.getAttribute("id") === "ios_user_count" ||
            e.currentTarget.getAttribute("id") === "android_user_count"
        ) {
            return;
        } else if (e.currentTarget.getAttribute("id") === "hotel_total") {
            return navigate("/category");
        } else if (e.currentTarget.getAttribute("id") === "upcoming_total") {
            return navigate("/upcoming-hotels");
        } else if (e.currentTarget.getAttribute("id") === "reviews") {
            return navigate("/reviews-table");
        } else if (e.currentTarget.getAttribute("id") === "transaction_avg") {
            return navigate("/transaction-duration-table");
        } else if (e.currentTarget.getAttribute("id") === "new_calls_count") {
            return navigate("/new-call-details");
        } else if (e.currentTarget.getAttribute("id") === "call_logs_count") {
            return navigate("/call-log");
        } else if (e.currentTarget.getAttribute("id") === "no_scan_count") {
            return navigate("/scan-details-table");
        } else if (e.currentTarget.getAttribute("id") === "spticket_count") {
            return navigate("/support-details-table");
        } else if (e.currentTarget.getAttribute("id") === "avg_total_time") {
            navigate("/total-time-duration");
        } else if (e.currentTarget.getAttribute("id") === "walkin_logs_count") {
            navigate("/walkIn-log");
        } else if (e.currentTarget.getAttribute("id") === "missed_call_rate") {
            navigate("/missed-call-rate");
        } else if (
            e.currentTarget.getAttribute("id") === "like_count" ||
            e.currentTarget.getAttribute("id") === "dislike_count"
        ) {
            navigate("/like-dislike-details", {
                state: {
                    from: e.currentTarget.getAttribute("id"),
                },
            });
        } else if (
            e.currentTarget.getAttribute("id") === "hotel_staff_engaged_count"
        ) {
            navigate("/frontdesk-connection-details");
        } else if (
            e.currentTarget.getAttribute("id") === "accepted_calls_counts"
        ) {
            navigate("/call-answered");
        } else if (
            e.currentTarget.getAttribute("id") === "missed_calls_counts"
        ) {
            navigate("/call-missed");
        } else if (
            e.currentTarget.getAttribute("id") === "avg_response_total_time"
        ) {
            navigate("/average_reponse_time");
        } else if (e.currentTarget.getAttribute("id") === "shift_notes_count") {
            navigate("/shift_notes");
        } else if (
            e.currentTarget.getAttribute("id") === "receipts_sent_counts"
        ) {
            setSelectedCheckinType(0);
            navigate("/sent_receipt");
        } else {
            if (
                url != "getSupportTicketErrorCounts" &&
                url != "getMoreSupportTicketErrorCounts"
            )
                navigate(`/details/${e.currentTarget.getAttribute("id")}`);
            else {
                console.log(e.currentTarget.getAttribute("data-component"));
                navigate(
                    `/quality-metrics-details/${e.currentTarget.getAttribute(
                        "id",
                    )}`,
                    {
                        state: {
                            component: e.currentTarget.querySelector(
                                ".dashboard-counts-card-header-title",
                            ).innerHTML,
                            component_name:
                                e.currentTarget.getAttribute("data-component"),
                        },
                    },
                );
            }
        }
    }

    return (
        <>
            {widgetData.length > 0 ? (
                widgetData.map((e) => {
                    const {
                        key,
                        value,
                        title,
                        Icon,
                        diff,
                        spticket_total_count,
                        excellent_count,
                        good_count,
                        poor_count,
                        excellent_count_percentage,
                        good_count_percentage,
                        poor_count_percentage,
                        info,
                        SVGIcon,
                    } = e;

                    return (
                        <Card
                            hoverable
                            id={key}
                            key={key}
                            onClick={getWidgetDetails}
                            className="dashboard-counts"
                            data-component={e?.componentName}
                        >
                            <div className="dashboard-counts-card-body">
                                <div className="flex mb-2">
                                    {SVGIcon || Icon ? (
                                        <div className="dashboard-counts-card-icon-wrap">
                                            {SVGIcon ? (
                                                SVGIcon
                                            ) : (
                                                <img
                                                    src={Icon}
                                                    alt={title}
                                                    className="dashboard-counts-card-icon-image"
                                                />
                                            )}
                                        </div>
                                    ) : null}
                                    <div className="dashboard-counts-diff">
                                        {spticket_total_count && (
                                            <Tag
                                                color={"var(--color-tags)"}
                                                className="margin-right-10"
                                            >
                                                {spticket_total_count}
                                            </Tag>
                                        )}

                                        {key !== "hotel_total" &&
                                        key !== "upcoming_total" &&
                                        key !== "call_logs_count" &&
                                        // key !== "hubspot_tickets_count" &&
                                        key !== "new_calls_count" &&
                                        key !== "reviews" &&
                                        key !== "transaction_avg" &&
                                        key !== "walkin_logs_count" &&
                                        key !== "spticket_count" &&
                                        key !== "walkin_logs_count" &&
                                        key !== "like_count" &&
                                        key !== "dislike_count" ? (
                                            Math.sign(diff) === 0 ||
                                            diff === undefined ? (
                                                <span className="dashboard-counts-diff-zero">
                                                    --
                                                </span>
                                            ) : Math.sign(diff) > 0 ? (
                                                <>
                                                    <span
                                                        className="dashboard-counts-diff-no"
                                                        style={{
                                                            color: "#70cb70",
                                                        }}
                                                    >
                                                        {`${Math.abs(
                                                            diff,
                                                        ).toFixed(2)} %`}
                                                    </span>
                                                    <ArrowUpOutlined
                                                        style={{
                                                            marginLeft: "2px",
                                                            color: "#70cb70",
                                                        }}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <span
                                                        className="dashboard-counts-diff-no"
                                                        style={{
                                                            color: "#ff4949",
                                                        }}
                                                    >
                                                        {`${Math.abs(
                                                            diff,
                                                        ).toFixed(2)} %`}
                                                    </span>
                                                    <ArrowDownOutlined
                                                        style={{
                                                            marginLeft: "2px",
                                                            color: "#ff4949",
                                                        }}
                                                    />
                                                </>
                                            )
                                        ) : (
                                            <></>
                                        )}
                                        {excellent_count && (
                                            <div className="display-flex-column margin-top-5 margin-left-5">
                                                <FaRegSmile className="text-green-600 text-lg mb-1" />
                                                <span className="font-semibold">
                                                    {excellent_count}
                                                </span>
                                                <span>
                                                    {`${excellent_count_percentage}%`}
                                                </span>
                                            </div>
                                        )}
                                        {good_count && (
                                            <div className="display-flex-column margin-top-5 margin-left-5">
                                                {/* <img
                                                    alt="review"
                                                    src={ConfusedIcon}
                                                    width="20px"
                                                /> */}
                                                <FaRegFaceMeh className="text-yellow-600 text-lg mb-1" />
                                                <span className="font-semibold">
                                                    {good_count}
                                                </span>
                                                <span>
                                                    {`${good_count_percentage}%`}
                                                </span>
                                            </div>
                                        )}
                                        {poor_count && (
                                            <div className="display-flex-column margin-top-5 margin-left-5">
                                                <FaRegFaceFrown className="text-red-600 text-lg mb-1" />
                                                <span className="font-semibold">
                                                    {poor_count}
                                                </span>
                                                <span>
                                                    {`${poor_count_percentage}%`}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="dashboard-counts-diff-new">
                                    <span className="dashboard-counts-card-body-value">
                                        {key === `cash_collected` ||
                                        key === `cash_dispence` ||
                                        key === `total_cash`
                                            ? `$${Number(value).toLocaleString(
                                                  "en-US",
                                              )}` // parse number to us standards for cash entries
                                            : key === "transaction_avg" ||
                                              key === "avg_total_time" ||
                                              key === "avg_response_total_time"
                                            ? value
                                            : Number(value)}
                                    </span>

                                    {/* {info && (
                                        <span className="dashboard-counts-card-info">
                                            <Tooltip
                                                placement="rightTop"
                                                title={info}
                                            >
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </span>
                                    )} */}
                                </div>
                                <span className="dashboard-counts-card-header-title">
                                    {title}
                                </span>
                                {/* <img
                                    src={Icon}
                                    alt={title}
                                    className="dashboard-counts-card-header-icon"
                                /> */}
                            </div>
                        </Card>
                    );
                })
            ) : status === "idle" || status === "loading" || isFetching ? (
                <WidgetSkeletons />
            ) : null}
        </>
    );
}

export default CustomWidgets;
